import { addMinutes } from "date-fns";
import qs from "query-string";
import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBookingsForLayer } from "@/api/hooks/useBookingsForLayer";
import { useMetadata } from "@/api/hooks/useMetadata";
import { useTree } from "@/api/hooks/useTree";
import ProjectMap from "@/components/shared/map/Map";
import { useToast } from "@/components/shared/toast/useToast";
import { useGlobalStore } from "@/stores/globalStore";
import { useMapStore } from "@/stores/mapStore";
import { useNavigationStore } from "@/stores/navigationStore";
import { useProjectStore } from "@/stores/projectStore";
import { formatLocalDateToAPI } from "@/utils/helpers/dates.helpers";
import AppLayout from "../components/layout/AppLayout";

const ProjectContainer: React.FC = () => {
	return (
		<AppLayout>
			<ProjectMap />
			<SideEffector />
		</AppLayout>
	);
};

export default ProjectContainer;

ProjectContainer.whyDidYouRender = true;

const SideEffector = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { enqueueToast } = useToast();

	// data selectors
	const activeLayer = useGlobalStore((state) => state.activeLayer);
	const employee = useGlobalStore((state) => state.employee);
	const seat = useGlobalStore((state) => state.seat);
	const nodes = useProjectStore((state) => state.nodes);
	const setActiveLayer = useGlobalStore((state) => state.setActiveLayer);
	const setEmployee = useGlobalStore((state) => state.setEmployee);
	const seatEmployee = useGlobalStore((state) => state.seatEmployee);
	const setSeatEmployee = useGlobalStore((state) => state.setSeatEmployee);
	const setSeat = useGlobalStore((state) => state.setSeat);
	const setZoomSeat = useMapStore((state) => state.setZoomSeat);
	const setNodes = useProjectStore((state) => state.setNodes);
	const setObjectTypes = useProjectStore((state) => state.setObjectTypes);
	const isRouteActive = useNavigationStore((state) => state.isRouteActive);

	const selection = useGlobalStore((state) => state.selection);
	const { data } = useBookingsForLayer(
		Number(activeLayer),
		formatLocalDateToAPI(selection.startDate),
		formatLocalDateToAPI(addMinutes(selection.endDate, -30)),
	);
	const bookingData = useMemo(
		() => data?.bookings?.find((book) => Number(book.point_id) === seat),
		[data, seat],
	);

	const { metadata } = useMetadata();
	const objectTypes =
		useMemo(
			() =>
				metadata?.nodes.map((node) => ({ value: node.uid, label: node.name })),
			[metadata?.nodes],
		) || [];

	const { layers } = useTree();
	const queryParams = useMemo(
		() => qs.parse(location.search),
		[location.search],
	);
	const mapID = queryParams.map;
	const employeeID = queryParams.employee;
	const pointID = queryParams.point;
	const warnedMapIdsRef = useRef(new Set<number>());

	useEffect(() => {
		setObjectTypes(objectTypes);
	}, [objectTypes, setObjectTypes]);

	useEffect(() => {
		if (layers) {
			if (layers.layer && !mapID) {
				setActiveLayer(Number(layers.layer.id));
			}
			setNodes(layers?.nodes);
		}
	}, [layers, mapID, setActiveLayer, setNodes]);

	useEffect(() => {
		if (mapID) {
			setActiveLayer(Number(mapID));
		}
		if (pointID) {
			// super experimental
			setTimeout(() => {
				setZoomSeat(Number(pointID));
			}, 100);
		}
	}, [mapID, pointID, setActiveLayer, setZoomSeat]);

	useEffect(() => {
		if (employeeID) {
			setEmployee(Number(employeeID));
		}
	}, [employeeID, setEmployee]);

	useEffect(() => {
		if (pointID) {
			setSeat(Number(pointID));
		}
	}, [pointID, setSeat]);

	useEffect(() => {
		if (bookingData && seatEmployee !== Number(bookingData.user_id)) {
			setSeatEmployee(Number(bookingData.user_id));
		}
	}, [bookingData, seatEmployee, setSeatEmployee]);

	useEffect(() => {
		const newQueries = { ...queryParams, employee, point: null };
		const nextSearch = qs.stringify(newQueries, {
			sort: false,
			skipNull: true,
		});
		const currentSearch = location.search.replace(/^\?/, "");

		if (nextSearch === currentSearch) return;

		navigate({
			pathname: location.pathname,
			search: nextSearch,
		});
	}, [employee, queryParams, location.pathname, location.search, navigate]);

	useEffect(() => {
		if (isRouteActive) return;

		const newQueries = {
			...queryParams,
			map: activeLayer,
			point: seat,
			employee: null,
		};
		const layer = nodes.find((node) => node.id === Number(activeLayer));

		if (!nodes.length) return;

		if (!layer) {
			const activeLayerId = Number(activeLayer);

			// Слой из ?map= может отсутствовать в дереве sidebar, но быть валидным в API
			if (mapID && Number(mapID) === activeLayerId) {
				const nextSearch = qs.stringify(newQueries, {
					sort: false,
					skipNull: true,
				});
				const currentSearch = location.search.replace(/^\?/, "");

				if (nextSearch !== currentSearch) {
					navigate({
						pathname: location.pathname,
						search: nextSearch,
					});
				}
				return;
			}

			const parentLayer = nodes.find((node) => !node.parent) || nodes[0];
			if (!parentLayer) return;

			if (!warnedMapIdsRef.current.has(activeLayerId)) {
				warnedMapIdsRef.current.add(activeLayerId);
				enqueueToast(
					{
						title: "Уровень не найден",
						message: "Запрашиваемый уровень не найден.",
					},
					{ variant: "error" },
				);
			}

			const fallbackQueries = {
				...queryParams,
				map: parentLayer.id,
				point: null,
				employee: null,
			};
			const fallbackSearch = qs.stringify(fallbackQueries, {
				sort: false,
				skipNull: true,
			});
			const currentSearch = location.search.replace(/^\?/, "");

			if (Number(activeLayer) !== parentLayer.id) {
				setActiveLayer(parentLayer.id);
			}

			if (fallbackSearch !== currentSearch) {
				navigate({
					pathname: location.pathname,
					search: fallbackSearch,
				});
			}
			return;
		}

		const nextSearch = qs.stringify(newQueries, {
			sort: false,
			skipNull: true,
		});
		const currentSearch = location.search.replace(/^\?/, "");

		if (nextSearch === currentSearch) return;

		navigate({
			pathname: location.pathname,
			search: nextSearch,
		});
	}, [
		seat,
		activeLayer,
		nodes,
		mapID,
		isRouteActive,
		queryParams,
		location.pathname,
		location.search,
		navigate,
		enqueueToast,
		setActiveLayer,
	]);

	return null;
};

// SideEffector.whyDidYouRender = true
